import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Badge, Alert, Modal, Table, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faLightbulb,
  faSave,
  faFolderOpen,
  faSpinner,
  faShieldAlt,
  faClipboardCheck,
  faFileContract,
  faDollarSign,
  faExclamationTriangle,
  faLink,
  faHome,
  faSignature,
  faUserTie,
  faHandshake,
  faCalendarCheck,
  faMapMarkerAlt,
  faPercentage,
  faChartLine,
  faSun
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';

interface DocumentTemplate {
  id: string;
  filename: string;
  description?: string;
  created_at: string;
  document_type: string;
  s3_url?: string;
}

interface ComplianceMapping {
  id?: string;
  brokerage_id: string;
  document_id: string;
  template_name: string;
  compliance_template: string;
  created_at?: string;
  updated_at?: string;
}

interface DocumentComplianceRulesSimplifiedProps {
  brokerageId: string;
  onSave?: (mapping: ComplianceMapping) => void;
}

// Human-friendly template descriptions
const complianceTemplates = {
  arizona_purchase_contract_2025: {
    id: 'arizona_purchase_contract_2025',
    title: "Arizona Purchase Contract Validation",
    state: "Arizona",
    association: "AAR",
    count: "31 critical checkpoints",
    icon: faFileContract,
    categories: [
      {
        name: "Financial Accuracy",
        icon: faDollarSign,
        items: [
          "Purchase price and earnest money amounts",
          "Proper earnest money handling instructions", 
          "Commission acknowledgments"
        ]
      },
      {
        name: "Required Disclosures",
        icon: faExclamationTriangle,
        items: [
          "Lead paint disclosure for pre-1978 properties",
          "Property condition disclosures",
          "Agency relationship confirmations"
        ]
      },
      {
        name: "Document Completeness",
        icon: faCheckCircle,
        items: [
          "All parties have signed and initialed appropriately",
          "Required addenda are attached",
          "Dates and deadlines are specified"
        ]
      },
      {
        name: "Cross-Document Validation",
        icon: faLink,
        items: [
          "Addenda mentioned in contract are included",
          "Lead paint disclosure matches property age",
          "Dual agency consent present when required"
        ]
      }
    ]
  },
  arizona_seller_disclosure_2025: {
    id: 'arizona_seller_disclosure_2025',
    title: "Arizona Seller Disclosure (SPDS) Validation",
    state: "Arizona",
    association: "AAR", 
    count: "15 critical checkpoints",
    icon: faClipboardCheck,
    categories: [
      {
        name: "Property Condition",
        icon: faHome,
        items: [
          "All disclosure questions answered completely",
          "Known defects properly disclosed",
          "Material facts documented"
        ]
      },
      {
        name: "Signatures & Dating",
        icon: faSignature,
        items: [
          "Seller signatures on all pages",
          "Proper dating throughout document",
          "Buyer acknowledgment of receipt"
        ]
      }
    ]
  },
  arizona_buyer_broker_exclusive_2025: {
    id: 'arizona_buyer_broker_exclusive_2025',
    title: "Arizona Buyer-Broker Exclusive Employment Agreement",
    state: "Arizona",
    association: "AAR",
    count: "23 critical checkpoints",
    icon: faUserTie,
    categories: [
      {
        name: "Agreement Terms",
        icon: faCalendarCheck,
        items: [
          "Exclusive representation period defined",
          "Geographic area specified",
          "Property type selected"
        ]
      },
      {
        name: "Compensation & Signatures",
        icon: faDollarSign,
        items: [
          "Compensation structure clearly stated",
          "Retainer fee terms if applicable",
          "All required initials and signatures"
        ]
      }
    ]
  },
  arizona_buyer_broker_show_property_2025: {
    id: 'arizona_buyer_broker_show_property_2025',
    title: "Arizona Buyer-Broker Agreement to Show Property",
    state: "Arizona", 
    association: "AAR",
    count: "14 critical checkpoints",
    icon: faHome,
    categories: [
      {
        name: "Limited Service Agreement",
        icon: faClipboardCheck,
        items: [
          "Property showing authorization",
          "Term dates specified",
          "Property type identified"
        ]
      },
      {
        name: "Compensation Terms",
        icon: faDollarSign,
        items: [
          "Compensation if purchase occurs",
          "Payment structure defined",
          "Required signatures present"
        ]
      }
    ]
  },
  arizona_listing_agreement_exclusive_2025: {
    id: 'arizona_listing_agreement_exclusive_2025',
    title: "Arizona Listing Contract - Exclusive Right to Sell/Rent",
    state: "Arizona",
    association: "AAR",
    count: "34 critical checkpoints",
    icon: faHome,
    categories: [
      {
        name: "Property & Pricing",
        icon: faMapMarkerAlt,
        items: [
          "Complete property description",
          "Sale and/or rental pricing",
          "Term dates and expiration"
        ]
      },
      {
        name: "Marketing & Permissions",
        icon: faClipboardCheck,
        items: [
          "MLS marketing authorization",
          "Sign, lockbox, and photo permissions",
          "Offer disclosure authorization"
        ]
      },
      {
        name: "Commission Structure",
        icon: faDollarSign,
        items: [
          "Listing broker compensation",
          "Buyer/tenant broker authorization",
          "Additional compensation terms"
        ]
      }
    ]
  },
  arizona_listing_contract_addendum_2024: {
    id: 'arizona_listing_contract_addendum_2024',
    title: "Arizona Listing Contract Addendum",
    state: "Arizona",
    association: "AAR",
    count: "13 critical checkpoints",
    icon: faFileContract,
    categories: [
      {
        name: "Modification Options",
        icon: faClipboardCheck,
        items: [
          "Compensation changes",
          "Term extension",
          "Other modifications"
        ]
      },
      {
        name: "Cancellation Terms",
        icon: faExclamationTriangle,
        items: [
          "Immediate termination option",
          "Conditional cancellation",
          "Mutual release provisions"
        ]
      }
    ]
  },
  arizona_short_sale_addendum_2017: {
    id: 'arizona_short_sale_addendum_2017',
    title: "Arizona Short Sale Addendum",
    state: "Arizona",
    association: "AAR",
    count: "8 critical checkpoints",
    icon: faExclamationTriangle,
    categories: [
      {
        name: "Required Disclosures",
        icon: faExclamationTriangle,
        items: [
          "Professional advice advisory",
          "Credit impact warnings",
          "Tax implications disclosure"
        ]
      },
      {
        name: "Process Requirements",
        icon: faClipboardCheck,
        items: [
          "Creditor approval needed",
          "Financial documentation required",
          "Extended timeline expected"
        ]
      }
    ]
  },
  arizona_buyer_advisory_2024: {
    id: 'arizona_buyer_advisory_2024',
    title: "Arizona Buyer Advisory",
    state: "Arizona",
    association: "AAR",
    count: "7 critical checkpoints",
    icon: faExclamationTriangle,
    categories: [
      {
        name: "Due Diligence Topics",
        icon: faClipboardCheck,
        items: [
          "Physical property inspections",
          "Environmental concerns and hazards",
          "Insurance requirements and claims history"
        ]
      },
      {
        name: "Buyer Acknowledgments",
        icon: faCheckCircle,
        items: [
          "Buyer signature and date",
          "Receipt acknowledgment checkbox",
          "Understanding of buyer responsibilities"
        ]
      }
    ]
  },
  arizona_fair_housing_advisory_2023: {
    id: 'arizona_fair_housing_advisory_2023',
    title: "Arizona Fair Housing Advisory",
    state: "Arizona",
    association: "AAR",
    count: "8 critical checkpoints",
    icon: faShieldAlt,
    categories: [
      {
        name: "Protected Classes",
        icon: faShieldAlt,
        items: [
          "Federal protected classes listed",
          "Arizona additional protections",
          "Discrimination prohibitions"
        ]
      },
      {
        name: "Compliance Requirements",
        icon: faExclamationTriangle,
        items: [
          "No discriminatory practices",
          "Equal treatment obligations",
          "Violation penalties disclosed"
        ]
      }
    ]
  },
  arizona_market_conditions_advisory_2024: {
    id: 'arizona_market_conditions_advisory_2024',
    title: "Arizona Market Conditions Advisory",
    state: "Arizona",
    association: "AAR",
    count: "8 critical checkpoints",
    icon: faChartLine,
    categories: [
      {
        name: "Market Factors",
        icon: faChartLine,
        items: [
          "Current market conditions disclosure",
          "Multiple offer scenarios",
          "Pricing strategy considerations"
        ]
      },
      {
        name: "Risk Disclosures",
        icon: faExclamationTriangle,
        items: [
          "Market volatility warnings",
          "No guarantee of predictions",
          "Client acknowledgment required"
        ]
      }
    ]
  },
  arizona_wire_fraud_advisory_2024: {
    id: 'arizona_wire_fraud_advisory_2024',
    title: "Arizona Wire Fraud Advisory",
    state: "Arizona",
    association: "AAR",
    count: "8 critical checkpoints",
    icon: faExclamationTriangle,
    categories: [
      {
        name: "Critical Warnings",
        icon: faExclamationTriangle,
        items: [
          "Wire fraud is irreversible",
          "Email is not secure",
          "Verification procedures mandatory"
        ]
      },
      {
        name: "Protection Measures",
        icon: faShieldAlt,
        items: [
          "Call to verify all instructions",
          "Red flags identification",
          "Client verification commitment"
        ]
      }
    ]
  },
  arizona_agency_disclosure_buyer_2024: {
    id: 'arizona_agency_disclosure_buyer_2024',
    title: "Arizona Agency Disclosure - Buyer",
    state: "Arizona",
    association: "AAR",
    count: "11 critical checkpoints",
    icon: faHandshake,
    categories: [
      {
        name: "Agency Election",
        icon: faHandshake,
        items: [
          "Single agency representation",
          "Dual agency consent option",
          "No representation option"
        ]
      },
      {
        name: "Disclosure Requirements",
        icon: faClipboardCheck,
        items: [
          "Agent duties explained",
          "Buyer signature required",
          "Election matches agreements"
        ]
      }
    ]
  },
  arizona_agency_disclosure_seller_2024: {
    id: 'arizona_agency_disclosure_seller_2024',
    title: "Arizona Agency Disclosure - Seller",
    state: "Arizona",
    association: "AAR",
    count: "12 critical checkpoints",
    icon: faHandshake,
    categories: [
      {
        name: "Agency Election",
        icon: faHandshake,
        items: [
          "Single agency representation",
          "Dual agency consent option",
          "No representation option"
        ]
      },
      {
        name: "Disclosure Requirements",
        icon: faClipboardCheck,
        items: [
          "Agent duties explained",
          "Seller signature required",
          "Property address included"
        ]
      }
    ]
  },
  arizona_generic_addendum_2024: {
    id: 'arizona_generic_addendum_2024',
    title: "Arizona Generic Addendum (#1-5)",
    state: "Arizona",
    association: "AAR",
    count: "10 critical checkpoints",
    icon: faFileContract,
    categories: [
      {
        name: "Contract References",
        icon: faLink,
        items: [
          "Original contract date and parties",
          "Property address matches",
          "Addendum properly numbered"
        ]
      },
      {
        name: "Terms & Signatures",
        icon: faClipboardCheck,
        items: [
          "Specific terms included",
          "Cannot be blank",
          "All parties sign and date"
        ]
      }
    ]
  },
  arizona_additional_clause_addendum_2024: {
    id: 'arizona_additional_clause_addendum_2024',
    title: "Arizona Additional Clause Addendum",
    state: "Arizona",
    association: "AAR",
    count: "11 critical checkpoints",
    icon: faFileContract,
    categories: [
      {
        name: "Clause Categories",
        icon: faClipboardCheck,
        items: [
          "Clause type selected",
          "Specific terms provided",
          "Supersedes notation if applicable"
        ]
      },
      {
        name: "Contract Integration",
        icon: faLink,
        items: [
          "References original contract",
          "No conflicting terms",
          "Properly incorporated"
        ]
      }
    ]
  },
  arizona_buyer_contingency_addendum_2023: {
    id: 'arizona_buyer_contingency_addendum_2023',
    title: "Arizona Buyer Contingency Addendum",
    state: "Arizona",
    association: "AAR",
    count: "15 critical checkpoints",
    icon: faExclamationTriangle,
    categories: [
      {
        name: "Contingency Terms",
        icon: faExclamationTriangle,
        items: [
          "Buyer's property address",
          "Sale or close contingency",
          "Deadline specified"
        ]
      },
      {
        name: "Kick-Out Provisions",
        icon: faClipboardCheck,
        items: [
          "Seller's right to market",
          "Notice period defined",
          "Current status documented"
        ]
      }
    ]
  },
  arizona_solar_addendum_2024: {
    id: 'arizona_solar_addendum_2024',
    title: "Arizona Solar Addendum",
    state: "Arizona",
    association: "AAR",
    count: "18 critical checkpoints",
    icon: faSun,
    categories: [
      {
        name: "System Ownership",
        icon: faSun,
        items: [
          "Owned, leased, or PPA",
          "Transfer requirements",
          "Financial obligations"
        ]
      },
      {
        name: "Technical Details",
        icon: faClipboardCheck,
        items: [
          "System specifications",
          "Production history",
          "Warranty transfers"
        ]
      }
    ]
  }
};

const DocumentComplianceRulesSimplified: React.FC<DocumentComplianceRulesSimplifiedProps> = ({ 
  brokerageId, 
  onSave 
}) => {
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [existingMappings, setExistingMappings] = useState<ComplianceMapping[]>([]);

  // Fetch template documents on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!brokerageId) return;

      setLoadingTemplates(true);
      try {
        // Fetch both template types
        const [templateResponse, contractResponse] = await Promise.all([
          axiosInstance.get('/documents/my-documents', {
            params: {
              owner_type: 'brokerage',
              brokerage_id: brokerageId,
              document_type: 'template'
            }
          }),
          axiosInstance.get('/documents/my-documents', {
            params: {
              owner_type: 'brokerage', 
              brokerage_id: brokerageId,
              document_type: 'contract_template'
            }
          })
        ]);
        
        const allTemplates = [
          ...(templateResponse.data.success ? templateResponse.data.documents : []),
          ...(contractResponse.data.success ? contractResponse.data.documents : [])
        ];
        
        setTemplates(allTemplates);
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
    fetchExistingMappings();
  }, [brokerageId]);

  const fetchExistingMappings = async () => {
    try {
      const response = await axiosInstance.get(`/compliance-mappings/${brokerageId}`);
      if (response.data.success) {
        setExistingMappings(response.data.mappings);
      }
    } catch (error) {
      console.log('No existing mappings found');
    }
  };

  const handleSave = async () => {
    if (!selectedDocument || !selectedTemplate) {
      toast.error('Please select both a document and a compliance template');
      return;
    }

    setSaving(true);
    try {
      const selectedDoc = templates.find(t => t.id === selectedDocument);
      const selectedCompliance = complianceTemplates[selectedTemplate as keyof typeof complianceTemplates];
      
      const mapping: ComplianceMapping = {
        brokerage_id: brokerageId,
        document_id: selectedDocument,
        template_name: selectedDoc?.filename || '',
        compliance_template: selectedTemplate
      };

      const response = await axiosInstance.post('/compliance-mappings', mapping);
      
      toast.success(`Successfully linked ${selectedDoc?.filename} to ${selectedCompliance.title}`);
      
      if (onSave) {
        onSave(response.data);
      }
      
      // Reset selections
      setSelectedDocument('');
      setSelectedTemplate('');
      fetchExistingMappings();
    } catch (error) {
      toast.error('Failed to save compliance mapping');
    } finally {
      setSaving(false);
    }
  };

  const detectTemplateType = (filename: string): string => {
    const lower = filename.toLowerCase();
    
    if (lower.includes('purchase') || lower.includes('contract') && !lower.includes('listing') || lower.includes('psa')) {
      return 'arizona_purchase_contract_2025';
    } else if (lower.includes('spds') || lower.includes('seller') && lower.includes('disclosure')) {
      return 'arizona_seller_disclosure_2025';
    } else if (lower.includes('buyer') && lower.includes('broker')) {
      if (lower.includes('exclusive') || lower.includes('employment')) {
        return 'arizona_buyer_broker_exclusive_2025';
      } else if (lower.includes('show') || lower.includes('showing')) {
        return 'arizona_buyer_broker_show_property_2025';
      } else {
        // Default to exclusive if not specified
        return 'arizona_buyer_broker_exclusive_2025';
      }
    } else if (lower.includes('listing')) {
      if (lower.includes('addendum')) {
        return 'arizona_listing_contract_addendum_2024';
      } else {
        // All listing agreements use the exclusive template
        return 'arizona_listing_agreement_exclusive_2025';
      }
    } else if (lower.includes('short') && lower.includes('sale')) {
      return 'arizona_short_sale_addendum_2017';
    } else if (lower.includes('buyer') && lower.includes('advisory')) {
      return 'arizona_buyer_advisory_2024';
    } else if (lower.includes('fair') && lower.includes('housing')) {
      return 'arizona_fair_housing_advisory_2023';
    } else if (lower.includes('market') && lower.includes('conditions')) {
      return 'arizona_market_conditions_advisory_2024';
    } else if (lower.includes('wire') && lower.includes('fraud')) {
      return 'arizona_wire_fraud_advisory_2024';
    } else if (lower.includes('agency') && lower.includes('disclosure')) {
      if (lower.includes('buyer')) {
        return 'arizona_agency_disclosure_buyer_2024';
      } else if (lower.includes('seller')) {
        return 'arizona_agency_disclosure_seller_2024';
      }
    } else if (lower.includes('solar')) {
      return 'arizona_solar_addendum_2024';
    } else if (lower.includes('buyer') && lower.includes('contingency')) {
      return 'arizona_buyer_contingency_addendum_2023';
    } else if (lower.includes('additional') && lower.includes('clause')) {
      return 'arizona_additional_clause_addendum_2024';
    } else if (lower.match(/addendum\s*#?\s*[1-5]/)) {
      // Match "Addendum #1", "Addendum 2", etc.
      return 'arizona_generic_addendum_2024';
    }
    
    return '';
  };

  return (
    <div className="document-compliance-rules-simplified">
      <Card className="mb-4 shadow">
        <Card.Header className="bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
              Document Compliance Configuration
            </h5>
            <Badge bg="light" text="dark">
              Simplified Setup
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          {/* Simplified Broker Experience */}
          <Alert variant="info" className="mb-4">
            <FontAwesomeIcon icon={faLightbulb} className="me-2" />
            <strong>Simple Setup:</strong> Select your uploaded document template below, and we'll automatically apply the appropriate compliance rules for Arizona. No configuration needed!
          </Alert>

          {/* Step 1: Document Selection */}
          <div className="mb-5">
            <h6 className="text-primary mb-3">
              <Badge bg="primary" className="me-2">1</Badge>
              Select Your Document Template
            </h6>
            <Form.Select
              size="lg"
              value={selectedDocument}
              onChange={(e) => {
                setSelectedDocument(e.target.value);
                if (e.target.value) {
                  const doc = templates.find(t => t.id === e.target.value);
                  if (doc) {
                    const detectedType = detectTemplateType(doc.filename);
                    if (detectedType) {
                      setSelectedTemplate(detectedType);
                    }
                  }
                }
              }}
              className="shadow-sm"
            >
              <option value="">Choose a document from your library...</option>
              {loadingTemplates ? (
                <option disabled>Loading your documents...</option>
              ) : (
                <>
                  {templates.length === 0 ? (
                    <option disabled>No documents uploaded yet - please upload documents first</option>
                  ) : (
                    templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.filename} {template.description ? `- ${template.description}` : ''}
                      </option>
                    ))
                  )}
                </>
              )}
            </Form.Select>
          </div>

          {/* Step 2: Template Selection (Auto-selected but can be changed) */}
          {selectedDocument && (
            <div className="mb-5">
              <h6 className="text-primary mb-3">
                <Badge bg="primary" className="me-2">2</Badge>
                Compliance Rules Template
              </h6>
              <Form.Select
                size="lg"
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="shadow-sm"
              >
                <option value="">Choose a compliance template...</option>
                {Object.entries(complianceTemplates).map(([key, template]) => (
                  <option key={key} value={key}>
                    {template.title} - {template.state} • {template.association} ({template.count})
                  </option>
                ))}
              </Form.Select>
              {selectedTemplate && complianceTemplates[selectedTemplate as keyof typeof complianceTemplates] && (
                <Alert variant="success" className="mt-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                  <strong>Selected:</strong> {complianceTemplates[selectedTemplate as keyof typeof complianceTemplates].title}
                </Alert>
              )}
            </div>
          )}

          {/* What We Check - Human Friendly */}
          {selectedTemplate && complianceTemplates[selectedTemplate as keyof typeof complianceTemplates] && (
            <Card className="mb-4 border-success">
              <Card.Header className="bg-success text-white">
                <FontAwesomeIcon icon={faClipboardCheck} className="me-2" />
                What We'll Check For You
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <h6>{complianceTemplates[selectedTemplate as keyof typeof complianceTemplates].title}</h6>
                  <Badge bg="success">
                    {complianceTemplates[selectedTemplate as keyof typeof complianceTemplates].count}
                  </Badge>
                </div>
                
                <Row>
                  {complianceTemplates[selectedTemplate as keyof typeof complianceTemplates].categories.map((category, index) => (
                    <Col md={6} key={index} className="mb-3">
                      <h6 className="text-success">
                        <FontAwesomeIcon icon={category.icon} className="me-2" size="sm" />
                        {category.name}
                      </h6>
                      <ul className="mb-0">
                        {category.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="text-muted" style={{ fontSize: '15px', lineHeight: '1.6' }}>{item}</li>
                        ))}
                      </ul>
                    </Col>
                  ))}
                </Row>
                
                <div className="mt-3 p-3 bg-light rounded">
                  <p className="text-muted mb-0">
                    <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                    <strong>Compliance Assurance:</strong> These validation rules are maintained by KairosIQ™ and automatically updated to match current Arizona real estate regulations. When issues are found, we'll provide specific line numbers and correction guidance.
                  </p>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Save Button */}
          <div className="text-center">
            <Button
              variant="primary"
              size="lg"
              onClick={handleSave}
              disabled={saving || !selectedDocument || !selectedTemplate}
              className="px-5"
            >
              <FontAwesomeIcon icon={faSave} className="me-2" />
              {saving ? 'Saving Configuration...' : 'Save Compliance Configuration'}
            </Button>
          </div>

          {/* Existing Mappings */}
          {existingMappings.length > 0 && (
            <Card className="mt-5">
              <Card.Header>
                <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
                Active Compliance Configurations
              </Card.Header>
              <Card.Body>
                <Table hover>
                  <thead>
                    <tr>
                      <th>Document Template</th>
                      <th>Compliance Rules</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingMappings.map((mapping, index) => (
                      <tr key={index}>
                        <td>{mapping.template_name}</td>
                        <td>
                          {complianceTemplates[mapping.compliance_template as keyof typeof complianceTemplates]?.title || mapping.compliance_template}
                        </td>
                        <td>
                          <Badge bg="success">Active</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default DocumentComplianceRulesSimplified;